/* eslint-disable react/display-name */
import PropTypes from 'prop-types';
import { useMemo, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { isMobile } from 'react-device-detect';
import { OverlayTrigger, Tooltip, Form, Spinner } from 'react-bootstrap';

import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';
import { hexToRGBA } from '_helpers/theme';

import Icon from '_components/atomic/Icon';

const { colors, fontSizes, spaces } = theme;

const StyledGroup = styled(
  forwardRef(({ labeled, bottomSpace, ...props }, ref) => (
    <Form.Group ref={ref} {...props} />
  ))
)`
  position: relative;
  padding-top: ${({ labeled }) => (labeled ? spaces.largeX : 0)}px;

  ${({ bottomSpace }) =>
    bottomSpace &&
    css`
      margin-bottom: ${bottomSpace}px;
    `}
`;

const StyledLabel = styled(Form.Label)`
  display: inline-flex;
  justify-content: flex-start;
  align-items: flex-end;
  gap: ${theme.spaces.small2X}px;

  position: absolute;
  top: 0;
  left: ${spaces.smallest}px;
  margin: 0;

  font-family: 'Manrope';
  font-size: ${fontSizes.small}px;
  font-style: normal;
  font-weight: 600;
  color: ${colors.gray[800]};

  span {
    cursor: pointer;
  }
`;

const StyledControl = styled(
  forwardRef(({ processing, ...props }, ref) => (
    <Form.Control ref={ref} {...props} />
  ))
)`
  min-height: 44px;
  width: 100%;
  resize: none;

  padding: ${spaces.smallX}px;

  border: 1px solid ${colors.gray[200]};
  border-radius: 6px;
  background-color: ${colors.white};

  font-family: 'Manrope';
  font-size: ${fontSizes.regular}px;
  font-style: normal;
  font-weight: 400;
  color: ${colors.gray[800]};
  caret-color: ${colors.indigo[500]};

  &::placeholder {
    color: ${colors.gray[500]};
  }

  &:focus {
    border: none;
    box-shadow: 0 1px 2px 0 ${hexToRGBA(theme.colors.indigo[600], 0.32)},
      0 0 0 1px ${theme.colors.indigo[600]}, 0 0 0 2px #fff,
      0 0 0 3px ${hexToRGBA(theme.colors.indigo[600], 0.24)};
  }

  &:disabled {
    background-color: ${colors.gray[100]};
  }

  &.form-control.is-valid {
    padding-right: ${spaces.regular}px;
    border-color: ${colors.gray[200]};
    background-image: none;

    &:focus {
      border: none;
      box-shadow: 0 1px 2px 0 ${hexToRGBA(theme.colors.indigo[600], 0.32)},
        0 0 0 1px ${theme.colors.indigo[600]}, 0 0 0 2px #fff,
        0 0 0 3px ${hexToRGBA(theme.colors.indigo[600], 0.24)};
    }
  }

  &.form-control.is-invalid {
    padding-right: ${spaces.regular}px;
    border-color: ${colors.orange[600]};
    background-image: none;

    &:focus {
      border: none;
      box-shadow: 0 1px 2px 0 ${hexToRGBA(theme.colors.orange[600], 0.32)},
        0 0 0 1px ${theme.colors.orange[600]}, 0 0 0 2px #fff,
        0 0 0 3px ${hexToRGBA(theme.colors.orange[600], 0.24)};
    }
  }

  ${({ processing }) =>
    processing &&
    css`
      padding-right: ${spaces.large6X}px;
    `};
`;

const StyledSpinner = styled(({ labeled, ...props }) => <Spinner {...props} />)`
  position: absolute;
  top: ${spaces.largeX + (44 - 32) / 2}px;
  right: ${spaces.smallX}px;

  &.text-custom {
    color: ${colors.gray[500]};
  }

  ${({ labeled }) =>
    !labeled &&
    css`
      top: ${(44 - 32) / 2}px;
    `}
`;

const StyledFeedback = styled(Form.Control.Feedback)`
  padding-left: ${spaces.smallest}px;

  font-family: 'Manrope';
  font-size: ${theme.fontSizes.smallX}px;
  font-style: normal;
  font-weight: 400;

  &.processing-feedback,
  &.valid-feedback,
  &.invalid-feedback {
    display: flex;
    justify-content: flex-start;
    align-items: stretch;
    gap: ${theme.spaces.small2X}px;

    span {
      display: flex;
      justify-content: center;
      align-items: flex-start;
    }
  }

  &.processing-feedback {
    color: ${colors.gray[500]};
  }

  &.valid-feedback {
    color: ${colors.indigo[500]};
  }

  &.invalid-feedback {
    color: ${colors.orange[600]};
  }
`;

const Textarea = forwardRef(
  (
    {
      name,
      hint,
      label,
      loading,
      labelSpace,
      required,
      bottomSpace,
      valid,
      invalid,
      feedback,
      value,
      onChange,
      ...restProps
    },
    ref
  ) => {
    const overlayTriggerProps = useMemo(() => {
      const props = {
        placement: isMobile ? 'top' : 'right-start',
        delay: { show: 0, hide: 0 },
        trigger: ['hover', 'focus'],
        overlay: (
          <Tooltip id={`tooltip-${name}`} className="hintTooltip">
            {hint}
          </Tooltip>
        ),
      };

      if (!hint || isMobile) {
        props.show = false;
      }

      return props;
    }, [name, hint]);

    return (
      <StyledGroup
        labeled={Boolean(label || labelSpace)}
        bottomSpace={bottomSpace}
        className={invalid ? 'custom-control-invalid' : ''}
      >
        {label && (
          <StyledLabel invalid={invalid}>
            {label}
            {hint && (
              <OverlayTrigger {...overlayTriggerProps}>
                <span>
                  <Icon
                    alias={ICONS_ALIASES.info}
                    color={theme.colors.gray[400]}
                  />
                </span>
              </OverlayTrigger>
            )}
          </StyledLabel>
        )}
        <StyledControl
          {...restProps}
          ref={ref}
          as="textarea"
          rows={5}
          isValid={valid}
          isInvalid={invalid}
          processing={loading}
          value={value || ''}
          onChange={onChange}
          data-hj-allow
        />
        {loading && (
          <>
            <StyledSpinner
              animation="border"
              variant="custom"
              labeled={Boolean(label || labelSpace)}
            />
            {feedback && (
              <StyledFeedback type="processing">{feedback}</StyledFeedback>
            )}
          </>
        )}
        {!loading && valid && feedback && (
          <StyledFeedback type="valid">{feedback}</StyledFeedback>
        )}
        {!loading && invalid && feedback && (
          <StyledFeedback type="invalid">
            <span>
              <Icon
                alias={ICONS_ALIASES.warning}
                color={theme.colors.orange[600]}
              />
            </span>
            {feedback}
          </StyledFeedback>
        )}
      </StyledGroup>
    );
  }
);

Textarea.propTypes = {
  name: PropTypes.string,
  hint: PropTypes.node,
  label: PropTypes.string,
  labelSpace: PropTypes.bool,
  loading: PropTypes.bool,
  required: PropTypes.bool,
  bottomSpace: PropTypes.number,
  valid: PropTypes.bool,
  invalid: PropTypes.bool,
  feedback: PropTypes.node,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

export default Textarea;
